/**
 * NoteAuditRow Component
 *
 * Single row displaying note audit summary information
 */

import React, { useMemo } from 'react';
import { Avatar } from '@jotpsych/ui';
import {
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronIcon,
  Description as NoteIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { NoteAuditListItem } from '../../types';
import { QualityScore } from '../shared/QualityScore';

/**
 * Parse patient name into first and last name
 */
function parsePatientName(patientName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  if (!patientName) return { firstName: '', lastName: '' };

  const parts = patientName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };

  // Take first part as first name, rest as last name
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

interface NoteAuditRowProps {
  /** Note audit data */
  audit: NoteAuditListItem;
  /** Click handler to view audit details */
  onViewAudit?: (noteId: string) => void;
  /** Click handler to open the actual note */
  onOpenNote?: (noteId: string) => void;
}

/**
 * Format date to short display format
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

/**
 * Format date to full display format for tooltips
 */
const formatFullDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export const NoteAuditRow: React.FC<NoteAuditRowProps> = ({ audit, onViewAudit, onOpenNote }) => {
  const theme = useTheme();

  const noteAudit = audit.audit;
  const revision = audit.current_revision;

  const handleViewAudit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewAudit?.(noteAudit.note_id);
  };

  const handleOpenNote = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenNote?.(noteAudit.note_id);
  };

  const handleRowClick = () => {
    onViewAudit?.(noteAudit.note_id);
  };

  const cptCodes = revision?.recommended_cpt_codes || [];
  const cptMatch = revision?.cpt_match;
  const compliancePct = noteAudit.latest_compliance_pct;

  const patientNameParts = useMemo(
    () => parsePatientName(noteAudit.patient_name),
    [noteAudit.patient_name],
  );
  const hasPatient = Boolean(noteAudit.patient_id);

  const isCompleted = noteAudit.is_completed;
  const completedAt = noteAudit.completed_at;

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '40px 70px 80px 180px 140px 70px 100px 60px 1fr 100px',
        gap: 2,
        alignItems: 'start',
        px: 2,
        py: 1.5,
        minWidth: 1080,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onViewAudit ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        '&:hover': onViewAudit
          ? {
              bgcolor: theme.palette.grey[50],
            }
          : {},
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Note Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: theme.palette.grey[100],
          color: theme.palette.grey[600],
        }}
      >
        <NoteIcon fontSize="small" />
      </Box>

      {/* Date */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatDate(noteAudit.last_audit_at)}
        </Typography>
      </Box>

      {/* Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        {isCompleted === true ? (
          <Tooltip
            title={completedAt ? `Completed on ${formatFullDate(completedAt)}` : 'Completed'}
            arrow
          >
            <Chip
              icon={<CheckCircleIcon />}
              label="Completed"
              size="small"
              color="success"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  fontSize: '1rem',
                },
              }}
            />
          </Tooltip>
        ) : isCompleted === false ? (
          <Chip
            label="Draft"
            size="small"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: theme.palette.grey[200],
              color: theme.palette.text.secondary,
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Patient Name with Avatar */}
      <Box>
        {hasPatient && noteAudit.patient_id ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Avatar
              id={noteAudit.patient_id}
              firstName={patientNameParts.firstName}
              lastName={patientNameParts.lastName}
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                fontWeight: 600,
                flexShrink: 0,
                mt: 0.5,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={noteAudit.patient_name || undefined}
              >
                {noteAudit.patient_name || '—'}
              </Typography>
              {noteAudit.note_title && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mt: 0.25,
                  }}
                  title={noteAudit.note_title}
                >
                  {noteAudit.note_title}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              —
            </Typography>
          </Box>
        )}
      </Box>

      {/* Provider Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={noteAudit.provider_name || undefined}
        >
          {noteAudit.provider_name || '—'}
        </Typography>
      </Box>

      {/* CPT Codes */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40 }}
      >
        {cptCodes.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
            {cptCodes.map((code, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  color:
                    cptMatch === false ? theme.palette.warning.main : theme.palette.text.primary,
                }}
              >
                {code}
                {index < cptCodes.length - 1 && ','}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontFamily: 'monospace',
              color: cptMatch === false ? theme.palette.warning.main : theme.palette.text.primary,
            }}
          >
            —
          </Typography>
        )}
        {cptMatch === false && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.warning.main, display: 'block' }}
          >
            Mismatch
          </Typography>
        )}
      </Box>

      {/* Compliance Score */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 40 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color:
              compliancePct !== null && compliancePct !== undefined
                ? compliancePct >= 80
                  ? theme.palette.success.main
                  : compliancePct >= 60
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        >
          {compliancePct !== null && compliancePct !== undefined
            ? `${compliancePct.toFixed(0)}%`
            : '—'}
        </Typography>
      </Box>

      {/* Quality Score */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        <QualityScore score={noteAudit.latest_quality_score} variant="badge" size="small" />
      </Box>

      {/* Spacer */}
      <Box />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minHeight: 40 }}>
        {onOpenNote && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpenNote}
            startIcon={<OpenIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            Note
          </Button>
        )}
        {onViewAudit && <ChevronIcon sx={{ color: theme.palette.grey[400] }} />}
      </Box>
    </Box>
  );
};

export default NoteAuditRow;
